import {useCallback, useEffect, useRef, useState} from 'react';

import {normalizeLoxoPodUrl} from './podUrl';
import {exchangeAuthTokens} from './requests/exchangeAuthTokens';
import {useRoamingSettings} from './RoamingSettingsContext';

const LOXO_LOGIN_BASE =
  'https://4134-98-32-20-94.ngrok-free.app?client=office_add_in';

type AuthCompleteMessage = {
  type: 'authComplete';
  pod_url: string;
  exchange_code: string;
};

type AuthErrorMessage = {
  type: 'authError';
  error?: string;
};

type AuthDialogMessage = AuthCompleteMessage | AuthErrorMessage;

type DialogMessageReceivedArg =
  | {message: string; origin?: string}
  | {error: number};

const dialogMessageArgToString = (
  arg: DialogMessageReceivedArg
): string | null => {
  if (!('message' in arg) || typeof arg.message !== 'string') {
    return null;
  }
  return arg.message;
};

const parseAuthDialogMessage = (raw: string): AuthDialogMessage | null => {
  try {
    const message = JSON.parse(raw) as AuthDialogMessage;
    if (message.type === 'authComplete') {
      if (
        typeof message.pod_url !== 'string' ||
        typeof message.exchange_code !== 'string'
      ) {
        return null;
      }
      return message;
    }
    if (message.type === 'authError') {
      return message;
    }
    return null;
  } catch {
    return null;
  }
};

export const useOfficeAuthDialog = () => {
  const {saveAuth} = useRoamingSettings();
  const [isOpening, setIsOpening] = useState(false);
  const [isExchanging, setIsExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<Office.Dialog | null>(null);
  const isHandlingAuthMessageRef = useRef(false);
  const parentOriginRef = useRef(
    typeof window !== 'undefined' ? window.location.origin : ''
  );

  const closeDialog = useCallback((dialog = dialogRef.current) => {
    if (dialogRef.current === dialog) {
      dialogRef.current = null;
    }
    if (dialog) {
      try {
        dialog.close();
      } catch {
        // Dialog may already be closed.
      }
    }
  }, []);

  const processAuthMessage = useCallback(
    (rawMessage: string) => {
      if (isHandlingAuthMessageRef.current) {
        return;
      }

      const message = parseAuthDialogMessage(rawMessage);
      if (!message) {
        return;
      }
      const dialog = dialogRef.current;

      if (message.type === 'authError') {
        setError(message.error ?? 'Sign-in failed.');
        closeDialog(dialog);
        return;
      }

      isHandlingAuthMessageRef.current = true;

      void (async () => {
        setIsExchanging(true);
        try {
          const safePodUrl = normalizeLoxoPodUrl(message.pod_url);
          const tokens = await exchangeAuthTokens(
            safePodUrl,
            message.exchange_code
          );
          await saveAuth({
            podUrl: safePodUrl,
            jwt: tokens.access_token,
            refreshToken: tokens.refresh_token,
          });
          setError(null);
        } catch (exchangeError) {
          const messageText =
            exchangeError instanceof Error
              ? exchangeError.message
              : 'Could not complete sign-in.';
          setError(messageText);
        } finally {
          isHandlingAuthMessageRef.current = false;
          setIsExchanging(false);
          closeDialog(dialog);
        }
      })();
    },
    [closeDialog, saveAuth]
  );

  const handleAuthDialogMessage = useCallback(
    (arg: DialogMessageReceivedArg) => {
      const rawMessage = dialogMessageArgToString(arg);
      if (!rawMessage) {
        return;
      }
      processAuthMessage(rawMessage);
    },
    [processAuthMessage]
  );

  useEffect(() => {
    const parentOrigin = window.location.origin;
    parentOriginRef.current = parentOrigin;

    const onWindowMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') {
        return;
      }
      if (event.origin !== new URL(LOXO_LOGIN_BASE).origin) {
        return;
      }
      processAuthMessage(event.data);
    };

    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [processAuthMessage]);

  const openLoginDialog = useCallback(() => {
    if (
      typeof Office === 'undefined' ||
      !Office.context?.ui?.displayDialogAsync
    ) {
      setError('Office dialog sign-in is not available in this environment.');
      return;
    }

    if (dialogRef.current || isHandlingAuthMessageRef.current) {
      return;
    }

    setError(null);
    setIsOpening(true);
    isHandlingAuthMessageRef.current = false;

    Office.context.ui.displayDialogAsync(
      LOXO_LOGIN_BASE,
      {
        height: 60,
        width: 35,
        displayInIframe: false,
      },
      (asyncResult) => {
        setIsOpening(false);

        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          setError('Could not open the Loxo sign-in window.');
          return;
        }

        const dialog = asyncResult.value;
        dialogRef.current = dialog;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          handleAuthDialogMessage
        );

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          if (dialogRef.current === dialog) {
            dialogRef.current = null;
          }
          setIsOpening(false);
          if (!isHandlingAuthMessageRef.current) {
            setIsExchanging(false);
          }
        });
      }
    );
  }, [handleAuthDialogMessage]);

  return {
    openLoginDialog,
    isOpening: isOpening || isExchanging,
    error,
  };
};
